import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import validator from "validator";

// Custom hooks
import useSocket from "src/hooks/useSocket";
import { useDialogBox } from "src/hooks/useDialogBox";
import { useAuctionsData } from "src/hooks/useAuctionsData";
import { useAuctionActions } from "src/hooks/useAuctionActions";
import { useDirtyFormGuard } from "src/hooks/useDirtyFormGuard";

// Layout/UI
import RecordNav from "../UI/RecordNav/RecordNav";
import PhotoManager from "../UI/Upload/PhotoManager";
import { NumericFormatAdapter } from "../UI/common/NumericFormatAdapter";
import { Box, Button, Field, Input, Stack } from "@metrea-ui/v2";
import FormTextField from "src/components/UI/common/FormTextField";

// Utilities
import { Session } from "src/utils/sessionUtils";
import { debugLog, errorLog } from "src/utils/debugUtils";
import { handleDecimalsOnValue, applyFormatToNumber, getShortDateTimeString } from "src/utils/formatUtils";
import { prepareAuctionPackageItemObject } from "src/utils/auctionUtils";
import { getDialogError } from "src/utils/sliceErrorUtils";

const getPriceError = (value) => {
	const normalizedValue = `${value ?? ""}`.trim();
	// Zero is a valid starting price before the first bid.
	return normalizedValue &&
		validator.isDecimal(normalizedValue) &&
		validator.isFloat(normalizedValue, { min: 0 })
		? ""
		: "A valid price is required! (i.e. 0 or greater)";
};

const toCents = (value) => Math.round(Number(value) * 100);

const getMaxPriceError = (value, currentPrice, increment, fundAssigned) => {
	const normalizedValue = `${value ?? ""}`.trim();
	if (!normalizedValue) return "";
	if (!validator.isDecimal(normalizedValue)) {
		return "A valid max price is required.";
	}
	if (
		toCents(normalizedValue) < toCents(currentPrice) + toCents(increment)
	) {
		return `Max Price must be at least Current Price + current increment ($${increment}).`;
	}
	if (parseFloat(normalizedValue) > fundAssigned) {
		return `Max Price cannot be greater than the bidder's assigned fund ($${fundAssigned.toFixed(2)}).`;
	}
	return "";
};

const getActiveAutoBidMaxPrice = (value, currentPrice, increment) => {
	if (value === "" || value == null) return "";
	const maxPrice = Number(value);
	const minimumNextBidCents = toCents(currentPrice) + toCents(increment);

	// A saved ceiling below the next valid bid is exhausted and should not look
	// like an active value that the bidder must repair before placing a new one.
	return Number.isFinite(maxPrice) && Number.isFinite(minimumNextBidCents) &&
		toCents(maxPrice) < minimumNextBidCents
		? ""
		: value;
};

const EditPackageItem = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;
	// Socket callbacks outlive the render that created them, so keep the latest
	// bid/fund state in refs to avoid stale-balance math during live updates.
	const currentPriceRef = useRef(0);
	const incrementRef = useRef(0);
	const fundAssignedRef = useRef(0);
	const highestBidderIdRef = useRef(null);
	const fundAvailableRef = useRef(0);
	const bidsListRef = useRef([]);
	const maxPriceRef = useRef("");
	const autoBidMaxIsSavedRef = useRef(true);
	const bidPendingRef = useRef(false);
	const bidRequestIdRef = useRef(0);
	const autoBidPendingRef = useRef(false);
	const autoBidRequestIdRef = useRef(0);
	const reconcileRequestIdRef = useRef(0);
	const bidUpdateDuringReconciliationRef = useRef(false);
	const socketReadyRef = useRef(false);
	const packageIdRef = useRef(props.aucPkg?.id);
	const [bidPending, setBidPending] = useState(false);
	const [autoBidPending, setAutoBidPending] = useState(false);

	const socketRef = useSocket();
	const [socketReady, setSocketReady] = useState(() => socketRef.current?.connected === true);

	const pageWidth = "95%";
	const pageMode = props.mode;
	const uiSize = props.size;
	const controlSize = uiSize === "large" ? "md" : "sm";

	const selectedPackageItem = useSelector((state) => state.auctions.selectedPkgItem);
	packageIdRef.current = props.aucPkg?.id;

	// --- Local State ---
	const [auctionItem, setAuctionItem] = useState(
		prepareAuctionPackageItemObject(props.aucPkg, selectedPackageItem)
	);
	const auctionItemIdRef = useRef(auctionItem?.id);
	const [bidsList, setBidsList] = useState(auctionItem?.auc_bids?.filter((x, idx) => idx < 10) || []);
	const [priceError, setPriceError] = useState(() => getPriceError(auctionItem?.current_price));
	const [maxPriceError, setMaxPriceError] = useState(() => {
		const activeMaxPrice = getActiveAutoBidMaxPrice(
			auctionItem?.myAutoBids?.[0]?.max_bid ?? "",
			auctionItem?.current_price,
			auctionItem?.increment
		);
		return getMaxPriceError(
			activeMaxPrice,
			auctionItem?.current_price,
			auctionItem?.increment,
			parseFloat(auctionItem?.myBidderInfo?.fund ?? 0)
		);
	});

	// --- Local state: Main package item, bids, errors ---
	const [currentPrice, setCurrentPrice] = useState(auctionItem?.current_price);
	const [increment, setIncrement] = useState(auctionItem?.increment);

	const [maxPrice, setMaxPrice] = useState(() =>
		getActiveAutoBidMaxPrice(
			auctionItem?.myAutoBids?.[0]?.max_bid ?? "",
			auctionItem?.current_price,
			auctionItem?.increment
		)
	);
	const [fundAvailable, setFundAvailable] = useState(auctionItem?.myBidderInfo?.fund_available ?? 0);
	const [highestBidderId, setHighestBidderId] = useState(auctionItem?.HighestBidder?.id ?? "");
	const [lastBidPlacedAt, setLastBidPlacedAt] = useState(auctionItem?.auc_bids?.[0]?.updatedAt ?? "");

	const formattedCurrentPrice =
		currentPrice === "" || currentPrice == null ? "" : Number(currentPrice).toFixed(2);
	const fundAssigned = parseFloat(auctionItem?.myBidderInfo?.fund ?? 0);
	const isCurrentUserLeading = highestBidderId === Session().currUser.id;
	const hasInsufficientFundsToBid =
		parseFloat(fundAvailable) < parseFloat(currentPrice) + parseFloat(increment);
	const isPlaceBidDisabled =
		pageMode === "edit" ||
		bidPending ||
		!socketReady ||
		isCurrentUserLeading ||
		auctionItem?.status_id !== 1 ||
		hasInsufficientFundsToBid;
	const placeBidLabel = !isCurrentUserLeading && hasInsufficientFundsToBid
		? "Not enough fund"
		: "Place Bid";

	useEffect(() => {
		currentPriceRef.current = Number(currentPrice || 0);
	}, [currentPrice]);

	useEffect(() => {
		incrementRef.current = Number(increment || 0);
	}, [increment]);

	useEffect(() => {
		highestBidderIdRef.current = highestBidderId;
	}, [highestBidderId]);

	useEffect(() => {
		fundAvailableRef.current = Number(fundAvailable || 0);
	}, [fundAvailable]);

	useEffect(() => {
		bidsListRef.current = bidsList;
	}, [bidsList]);

	useEffect(() => {
		maxPriceRef.current = maxPrice;
	}, [maxPrice]);

	// Custom hooks
	const { showDialog, withError } = useDialogBox();
	const {
		clearSelectedPackageItem,
		fetchAuctionPackages,
		fetchAuctionPackageByIdOrUuid,
		refreshAuctionLiveState,
		setSelectedPackage,
		setSelectedPackageItem,
	} = useAuctionsData();

	const { updatePackageItemAction } = useAuctionActions({
		fetchAuctionPackageByIdOrUuid,
		fetchAuctionPackages,
		// props.aucPkg = current package, keep parity with hook shape
		localAuctionPackage: props.aucPkg,
		auctionItem,
		setAuctionItem,
	});

	const applyPreparedPackageItem = (preparedPackageItem) => {
		debugLog(3, "[EditPackageItem][STATE] applyPreparedPackageItem", {
			propsAucPkg: props.aucPkg,
			preparedPackageItem: preparedPackageItem,
		});
		setAuctionItem(preparedPackageItem);
		auctionItemIdRef.current = preparedPackageItem?.id;
		const nextBidsList = preparedPackageItem?.auc_bids?.filter((x, idx) => idx < 10) || [];
		bidsListRef.current = nextBidsList;
		setBidsList(nextBidsList);
		setCurrentPrice(preparedPackageItem?.current_price ?? "");
		currentPriceRef.current = Number(preparedPackageItem?.current_price || 0);
		setIncrement(preparedPackageItem?.increment ?? 0);
		incrementRef.current = Number(preparedPackageItem?.increment || 0);
		fundAssignedRef.current = parseFloat(preparedPackageItem?.myBidderInfo?.fund ?? 0);
		const activeMaxPrice = getActiveAutoBidMaxPrice(
			preparedPackageItem?.myAutoBids?.[0]?.max_bid ?? "",
			preparedPackageItem?.current_price,
			preparedPackageItem?.increment
		);
		setMaxPrice(activeMaxPrice);
		maxPriceRef.current = activeMaxPrice;
		autoBidMaxIsSavedRef.current = true;
		setPriceError(getPriceError(preparedPackageItem?.current_price));
		setMaxPriceError(
			getMaxPriceError(
				activeMaxPrice,
				preparedPackageItem?.current_price,
				preparedPackageItem?.increment,
				parseFloat(preparedPackageItem?.myBidderInfo?.fund ?? 0)
			)
		);
		setFundAvailable(preparedPackageItem?.myBidderInfo?.fund_available ?? 0);
		fundAvailableRef.current = Number(preparedPackageItem?.myBidderInfo?.fund_available || 0);
		const nextHighestBidderId = preparedPackageItem?.HighestBidder?.id ?? "";
		highestBidderIdRef.current = nextHighestBidderId;
		setHighestBidderId(nextHighestBidderId);
		setLastBidPlacedAt(preparedPackageItem?.auc_bids?.[0]?.updatedAt ?? "");
		renderLiveAuctionUpdates(nextBidsList);
	};
	const applyPreparedPackageItemRef = useRef(applyPreparedPackageItem);
	applyPreparedPackageItemRef.current = applyPreparedPackageItem;

	// Effect: Sync/prepare the local auction item
	useEffect(() => {
		const preparedPackageItem = prepareAuctionPackageItemObject(props.aucPkg, selectedPackageItem);
		applyPreparedPackageItemRef.current(preparedPackageItem);
	}, [props.aucPkg, selectedPackageItem]);

	const originalItemSnapshot = useMemo(() => {
		const preparedPackageItem = prepareAuctionPackageItemObject(props.aucPkg, selectedPackageItem);
		return {
			auction_note: preparedPackageItem?.auction_note ?? "",
			current_price:
				preparedPackageItem?.current_price === "" || preparedPackageItem?.current_price == null
					? ""
					: Number(preparedPackageItem.current_price).toFixed(2),
		};
	}, [props.aucPkg, selectedPackageItem]);

	const localItemSnapshot = useMemo(
		() => ({
			auction_note: auctionItem?.auction_note ?? "",
			current_price:
				formattedCurrentPrice === "" || formattedCurrentPrice == null ? "" : formattedCurrentPrice,
		}),
		[auctionItem, formattedCurrentPrice]
	);

	const hasValidationError = priceError !== "" || maxPriceError !== "";
	const { canSave, canRevert } = useDirtyFormGuard({
		originalValue: originalItemSnapshot,
		currentValue: localItemSnapshot,
		hasError: hasValidationError,
	});

	// Auction item plain-text fields now share the stable direct-v2 helper, while
	// numeric/live-auction behavior remains local to this screen.
	const renderTextField = (props) => (
		<FormTextField {...props} size={controlSize} helperColor="#F04438" />
	);

	const renderNumericField = ({
		id,
		name,
		label,
		value,
		onChange,
		disabled = false,
		errorText = "",
		placeholder,
		width = "100%",
		formatSpec,
	}) => {
		const invalid = Boolean(errorText);
		const helpId = `${id}-helptext`;
		const helperVisible = Boolean(errorText);
		const controlStyle = {
			width: "100%",
			fontSize: controlSize === "sm" ? "0.8rem" : "0.875rem",
		};

		return (
			<Box width={width} mt="1rem" mb="0.5rem">
				<Field.Root invalid={invalid} style={{ width: "100%" }}>
					<Field.Label htmlFor={id}>{label}</Field.Label>
					<NumericFormatAdapter
						customInput={Input}
						id={id}
						name={name}
						value={value ?? ""}
						disabled={disabled}
						placeholder={placeholder}
						size={controlSize}
						variant="subtle"
						aria-invalid={invalid ? "true" : undefined}
						aria-describedby={helpId}
						onChange={onChange}
						formatSpec={formatSpec}
						style={controlStyle}
					/>
					<Field.HelperText id={helpId}>
						<span
							style={{
								display: "block",
								minHeight: "1.5em",
								fontSize: "0.75rem",
								color: invalid ? "#F04438" : "inherit",
								visibility: helperVisible ? "visible" : "hidden",
							}}
						>
							{helperVisible ? errorText : "\u00A0"}
						</span>
					</Field.HelperText>
				</Field.Root>
			</Box>
		);
	};

	useEffect(() => {
		if (!socketRef.current) return;
		const socket = socketRef.current;
		const initiallyConnected = socket.connected === true;
		socketReadyRef.current = initiallyConnected;
		setSocketReady(initiallyConnected);

		const reconcileLiveState = async () => {
			if (!socket.connected) return;
			const itemId = auctionItemIdRef.current;
			const packageId = packageIdRef.current;
			if (!itemId || !packageId) return;

			const requestId = ++reconcileRequestIdRef.current;
			socketReadyRef.current = false;
			setSocketReady(false);
			try {
				let refreshedPackage;
				let packageItem;
				do {
					bidUpdateDuringReconciliationRef.current = false;
					({ package: refreshedPackage, packageItem } = await refreshAuctionLiveState(
						packageId,
						itemId
					));
				} while (
					bidUpdateDuringReconciliationRef.current &&
					socket.connected &&
					requestId === reconcileRequestIdRef.current
				);
				if (
					requestId !== reconcileRequestIdRef.current ||
					!socket.connected ||
					Number(packageIdRef.current) !== Number(packageId) ||
					Number(auctionItemIdRef.current) !== Number(itemId)
				) {
					return;
				}
				// Commit the snapshot to shared selection only after the request-token and
				// current route checks pass; stale reads must not replace another selection.
				setSelectedPackage(refreshedPackage);
				setSelectedPackageItem(packageItem);
				applyPreparedPackageItemRef.current(
					prepareAuctionPackageItemObject(refreshedPackage, packageItem)
				);
				socketReadyRef.current = true;
				setSocketReady(true);
			} catch (error) {
				if (requestId !== reconcileRequestIdRef.current || !socket.connected) return;
				const refreshError = getDialogError(
					error,
					"Auction data could not be refreshed after reconnecting."
				);
				showDialog({
					title: "Auction Refresh Failed",
					message: refreshError.message,
					type: "error",
					// Reconciliation is a read-only operation, so always offer a retry.
					retryCallback: reconcileLiveState,
				});
			}
		};

		const handleConnect = () => {
			// A reconnect may follow a committed action whose broadcast was missed.
			reconcileLiveState();
		};
		const handleDisconnect = () => {
			socketReadyRef.current = false;
			reconcileRequestIdRef.current += 1;
			setSocketReady(false);
			// A disconnected socket cannot deliver either acknowledgment. Invalidate
			// old callbacks so they cannot clear later requests after reconnect.
			bidRequestIdRef.current += 1;
			bidPendingRef.current = false;
			setBidPending(false);
			autoBidRequestIdRef.current += 1;
			autoBidPendingRef.current = false;
			setAutoBidPending(false);
		};
		socket.on("connect", handleConnect);
		socket.on("disconnect", handleDisconnect);

		socket.on("bidUpdate", (bidPlaced) => {
			// Bid updates are broadcast to every socket. Only the open item may
			// change this item's price, bidder balance, or saved auto-bid display.
			if (Number(bidPlaced?.auctionId) !== Number(auctionItemIdRef.current)) return;
			if (!socketReadyRef.current) {
				// Do not apply a broadcast over REST data mid-refresh; repeat the
				// authoritative reads after the current refresh cycle instead.
				bidUpdateDuringReconciliationRef.current = true;
				return;
			}
			debugLog(9, "[EditPackageItem][FLOW] bidUpdate received", {
				bidPlaced: bidPlaced,
				bidsList: bidsListRef.current,
				auctionItem: auctionItem,
				fundAvailable: fundAvailableRef.current,
			});

			let newBid = parseFloat(bidPlaced.newBid);
			let fund = fundAvailableRef.current;

			// Mirror the backend fund-balance rule in the live view:
			// subtract the new winning price for the new leader, then refund the
			// previous winning price if the current viewer just lost the lead.
			// remove fund from the current highest bidder
			if (bidPlaced.highestBidderId === Session().currUser.id) {
				fund -= newBid;

				debugLog(9, "[EditPackageItem][STATE] fund removed from current highest bidder", {
					fund: fund,
				});
			}

			// return fund to the previous highest bidder
			if (highestBidderIdRef.current === Session().currUser.id) {
				fund += currentPriceRef.current;

				debugLog(9, "[EditPackageItem][STATE] fund returned to previous highest bidder", {
					fund: fund,
				});
			}

			const nextIncrement = parseFloat(bidPlaced.increment);
			currentPriceRef.current = newBid;
			incrementRef.current = nextIncrement;
			const nextMaxPrice = autoBidMaxIsSavedRef.current
				? getActiveAutoBidMaxPrice(maxPriceRef.current, newBid, nextIncrement)
				: maxPriceRef.current;
			maxPriceRef.current = nextMaxPrice;
			setMaxPrice(nextMaxPrice);
			setMaxPriceError(getMaxPriceError(nextMaxPrice, newBid, nextIncrement, fundAssignedRef.current));
			setCurrentPrice(bidPlaced.newBid);
			setIncrement(nextIncrement);
			setHighestBidderId(bidPlaced.highestBidderId);
			setLastBidPlacedAt(Date.now());

			setFundAvailable(fund);

			renderLiveAuctionUpdates(bidsListRef.current, bidPlaced);
		});

		socket.on("autoBidConfirmed", (autoBidPlaced) => {
			debugLog(9, "[EditPackageItem][FLOW] autoBidConfirmed received", {
				autoBidPlaced: autoBidPlaced,
			});

			if (autoBidPlaced.userId === Session().currUser.id) {
				const activeMaxPrice = getActiveAutoBidMaxPrice(
					autoBidPlaced.maxBid,
					currentPriceRef.current,
					incrementRef.current
				);
				autoBidMaxIsSavedRef.current = true;
				maxPriceRef.current = activeMaxPrice;
				setMaxPrice(activeMaxPrice);
				setMaxPriceError(
					getMaxPriceError(
						activeMaxPrice,
						currentPriceRef.current,
						incrementRef.current,
						fundAssignedRef.current
					)
				);
				showDialog({
					title: "Successful",
					message: [`The Auto Bid was received successfully!`],
				});
			}
		});

		socket.on("error", (error) => {
			errorLog(2, "[EditPackageItem][FLOW] socket error", { error: error });
			withError(error, null, {
				title: "Auction Action Failed",
				fallbackMessage: "The Auction action could not be completed.",
			});
		});

		return () => {
			reconcileRequestIdRef.current += 1;
			bidRequestIdRef.current += 1;
			autoBidRequestIdRef.current += 1;
			socket.off("connect", handleConnect);
			socket.off("disconnect", handleDisconnect);
			socket.off("bidUpdate");
			socket.off("autoBidConfirmed");

			// eslint-disable-next-line react-hooks/exhaustive-deps
			socket.off("error");
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		socketRef,
		withError,
		showDialog,
		refreshAuctionLiveState,
		setSelectedPackage,
		setSelectedPackageItem,
	]);

	// --- Handlers ---
	const handleInputChange = (event) => {
		const { name, value } = event.target;
		debugLog(7, "[EditPackageItem][ACTION] handleInputChange", { name: name, value: value });

		// only setAuctionItem when it's saved - 5/7/2025
		setAuctionItem({
			...auctionItem,
			[name]: value,
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		});

		if (name === "current_price") {
			const nextPriceError = getPriceError(value);
			setPriceError(nextPriceError);
			setCurrentPrice(value);
			setMaxPriceError(getMaxPriceError(maxPrice, value, increment, fundAssigned));
			if (!nextPriceError) {
				const decimalValue = handleDecimalsOnValue(value.trim());
				setCurrentPrice(decimalValue);
			}
		} else if (name === "max_price") {
			// Auto-bid ceilings can match the next valid increment, but they should
			// never fall below it or exceed the bidder's total assigned fund.
			const nextMaxPriceError = getMaxPriceError(value, currentPrice, increment, fundAssigned);
			autoBidMaxIsSavedRef.current = false;
			maxPriceRef.current = value;
			setMaxPriceError(nextMaxPriceError);
			setMaxPrice(value);
			if (!nextMaxPriceError) {
				const decimalValue = handleDecimalsOnValue(value);
				setMaxPrice(decimalValue);
			}
		}
	};

	// delegate to hook action (keeps UI clean, matches Baseline pattern)
	const clickSaveHandler = () => {
		debugLog(3, "[EditPackageItem][ACTION] clickSaveHandler", {
			auctionItem,
		});
		debugLog(2, "[EditPackageItem][FLOW] updatePackageItemAction started", {
			packageItemId: auctionItem?.id,
		});
		updatePackageItemAction();
	};

	const clickRevertHandler = () => {
		const preparedPackageItem = prepareAuctionPackageItemObject(props.aucPkg, selectedPackageItem);
		debugLog(3, "[EditPackageItem][ACTION] clickRevertHandler", {
			packageItemId: preparedPackageItem?.id,
		});

		applyPreparedPackageItem(preparedPackageItem);
	};

	const renderLiveAuctionUpdates = (arrayList, bidPlaced) => {
		debugLog(4, "[EditPackageItem][PROCESS] renderLiveAuctionUpdates", {
			arrayList: arrayList,
			bidPlaced: bidPlaced,
			auctionItem: auctionItem,
		});

		if (bidPlaced) {
			if (arrayList.length >= 10) arrayList.pop();
			const tempObj = {
				amount: bidPlaced.newBid,
				user: { full_name: bidPlaced.highestBidderName },
			};
			arrayList.unshift(tempObj);
			setBidsList([...arrayList]);
		}

		const ul = document.getElementById("updates");
		if (ul) {
			ul.innerHTML = "";
			arrayList.map((a, idx) => {
				const li = document.createElement("li");
				li.textContent = `
				New price ${applyFormatToNumber(parseFloat(a.amount), 2, "$", null, false)} \
				- ${a.user.full_name}`;
				ul.append(li);
				return false;
			});
		}
	};

	const clickBackToListHandler = () => {
		// socket.disconnect();
		debugLog(3, "[EditPackageItem][ACTION] clickBackToListHandler", { auctionItem: auctionItem });
		debugLog(2, "[EditPackageItem][FLOW] fetchAuctionPackageByIdOrUuid started", {
			idOrUuid: auctionItem?.package_id,
		});

		fetchAuctionPackageByIdOrUuid(auctionItem.package_id);
		clearSelectedPackageItem();
	};

	const clickBidHandler = (event) => {
		event.preventDefault();
		// Guard synchronously: React has not rerendered the disabled button yet
		// when a user double-clicks (or a row lock keeps the request waiting).
		if (
			bidPendingRef.current ||
			!socketReadyRef.current ||
			!socketRef.current?.connected
		) return;
		bidPendingRef.current = true;
		setBidPending(true);
		const requestId = ++bidRequestIdRef.current;
		debugLog(3, "[EditPackageItem][ACTION] clickBidHandler", {
			auctionItem: auctionItem,
			currentPrice: currentPrice,
		});

		const bidData = {
			auctionId: auctionItem.id,
			userId: Session().currUser.id,
			userName: Session().currUser.full_name,
		};
		socketRef.current.emit("placeBid", bidData, () => {
			// Only completion matters here; bidUpdate refreshes the view and the
			// socket error event reports rejection, so we do not inspect success.
			if (requestId !== bidRequestIdRef.current) return;
			bidPendingRef.current = false;
			setBidPending(false);
		});
	};

	const clickAutoBidHandler = (event) => {
		event.preventDefault();
		// Keep a rapid second click from queuing another ceiling update before
		// the first socket request finishes.
		if (
			autoBidPendingRef.current ||
			!socketReadyRef.current ||
			!socketRef.current?.connected
		) return;
		autoBidPendingRef.current = true;
		setAutoBidPending(true);
		const requestId = ++autoBidRequestIdRef.current;
		debugLog(3, "[EditPackageItem][ACTION] clickAutoBidHandler", {
			auctionItem: auctionItem,
			currentPrice: currentPrice,
			maxPrice: maxPrice,
			highestBidderId: highestBidderId,
		});

		const autoBidData = {
			auctionId: auctionItem.id,
			userId: Session().currUser.id,
			userName: Session().currUser.full_name,
			currBid: parseFloat(currentPrice),
			maxBid: parseFloat(maxPrice),
			isHighestBidder: highestBidderId === Session().currUser.id,
		};
		socketRef.current.emit("setAutoBid", autoBidData, () => {
			// Only release the pending guard here. autoBidConfirmed shows success;
			// the socket error event handles rejection.
			if (requestId !== autoBidRequestIdRef.current) return;
			autoBidPendingRef.current = false;
			setAutoBidPending(false);
		});
	};

	// --- DebugLog: Render & state snapshot ---
	debugLog(9, "[EditPackageItem][FLOW] render", {
		renderCount: renderCount.current,
		packageItemId: auctionItem?.id,
		pageMode: pageMode,
		bidCount: bidsList?.length ?? 0,
		currentPrice: currentPrice,
		maxPrice: maxPrice,
		fundAvailable: fundAvailable,
		highestBidderId: highestBidderId,
		lastBidPlacedAt: lastBidPlacedAt,
		priceError: priceError,
		maxPriceError: maxPriceError,
		uiSize: uiSize,
	});

	return (
		<div>
			{auctionItem && (
				<>
					<RecordNav
						callFrom={pageMode === "edit" ? "Package Item" : "Auction Item"}
						showBack
						{...(pageMode === "edit" ? { showRevert: true, showSave: true } : {})}
						// {...(isAdmin() && pageMode !== "edit" ? { showEdit: true } : {})}
						onClickBackToList={clickBackToListHandler}
						onClickRevert={clickRevertHandler}
						onClickSave={clickSaveHandler}
						disabledRevert={!canRevert}
						disabledSave={!canSave}
						hasError={hasValidationError}
					/>
					<hr />
					<Box m={3} />

					<Stack>
						<Box w={pageWidth} display="flex">
							<Box flex="1 1 60%" mr={2}>
								{renderTextField({
									id: "name",
									name: "name",
									label: "Item Name",
									required: true,
									value: auctionItem?.baseline_item?.name ?? "",
									disabled: true,
								})}

								{renderTextField({
									id: "description",
									name: "description",
									label: "Description",
									value: auctionItem?.baseline_item?.description ?? "",
									disabled: true,
									multiline: true,
									rows: 12,
								})}
							</Box>
							<Box flex="1 1 40%">
								{auctionItem?.id && (
									<PhotoManager
										item={auctionItem?.baseline_item}
										editable={false}
										title="Item Photo"
									/>
								)}
							</Box>
						</Box>

						{renderTextField({
							id: "auction_note",
							name: "auction_note",
							label: "Auction Notes",
							value: auctionItem?.auction_note ?? "",
							onChange: handleInputChange,
							disabled: pageMode !== "edit",
							multiline: true,
							rows: 8,
						})}

						<Box width="40%">
							{renderNumericField({
								id: "current_price",
								name: "current_price",
								label: auctionItem?.id ? "Current Price" : "Starting Price",
								placeholder: auctionItem?.id ? "Current Price" : "Starting Price",
								value: formattedCurrentPrice,
								onChange: handleInputChange,
								disabled: auctionItem?.auc_bids?.length > 0 || pageMode !== "edit",
								errorText: priceError,
								formatSpec: {
									kind: "currency",
									symbol: "$",
									unit: "",
									decimals: 2,
									fixed: true,
									allowNegative: false,
								},
							})}

							{pageMode === "liveAuction" && (
								<>
									{/* Keep both live-auction actions on direct Metrea buttons. */}
									<Button
										padding="10px"
										width="full"
										onClick={clickBidHandler}
										disabled={isPlaceBidDisabled}
									>
										{placeBidLabel}
									</Button>
									<div className="text-xs font-normal ">
										Fund balance:{" "}
										{`${applyFormatToNumber(
											parseFloat(fundAvailable),
											2,
											"$",
											null,
											false
										)}`}
									</div>
								</>
							)}

							<Box flex="1 1 30px">
								{/* <div className="text-lg font-semibold">Auto Bid</div> */}
							</Box>

							{pageMode === "liveAuction" && (
								<>
									{renderNumericField({
										id: "max_price",
										name: "max_price",
										label: "Auto Bid Maximum Price",
										placeholder: "Auto Bid Maximum Price",
										value: maxPrice,
										onChange: (event, sourceInfo) => {
											// Loading a saved value is not a bidder edit; only user input
											// should protect the field from live ceiling expiry.
											if (sourceInfo?.source !== "prop") handleInputChange(event);
										},
										disabled:
											pageMode === "edit" ||
											!socketReady ||
											auctionItem?.status_id !== 1 ||
											parseFloat(fundAvailable) <
												parseFloat(currentPrice) + parseFloat(increment),
										errorText: pageMode !== "edit" ? maxPriceError : "",
										formatSpec: {
											kind: "currency",
											symbol: "$",
											unit: "",
											decimals: 2,
											fixed: true,
											allowNegative: false,
										},
									})}

									<Button
										padding="10px"
										width="full"
										{...(pageMode === "edit" ||
											auctionItem?.status_id !== 1 ||
											autoBidPending ||
											!socketReady
											? { disabled: true }
											: {})}
										{...(parseFloat(maxPrice) <= parseFloat(currentPrice)
											? { disabled: true }
											: {})}
										{...(maxPrice === "" || maxPriceError !== ""
											? { disabled: true }
											: {})}
										onClick={clickAutoBidHandler}
									>
										{(auctionItem?.myAutoBids?.length > 0 ? "Update" : "Place") +
											" Auto Bid"}
									</Button>
								</>
							)}
						</Box>

						<Box m={3} />
					</Stack>
				</>
			)}
			{auctionItem?.status_id === 1 && (
				<Box>
					<Box
						my="2rem"
						display="flex"
						alignItems="flex-start"
						w={pageWidth}
						flexDirection="column"
					>
						<div className="text-xl font-semibold">Live Auction Updates</div>
						{auctionItem?.auc_bids.length > 0 && (
							<div className="text-xs font-normal">
								Last bid placed at {getShortDateTimeString(lastBidPlacedAt)}
							</div>
						)}
					</Box>
					<Box minH="300px" fontSize="0.8rem">
						<ul id="updates"></ul>
					</Box>
				</Box>
			)}
		</div>
	);
};

export default EditPackageItem;
